/**
 * Surface Persona Configuration
 *
 * Specialized persona for surface creation and detection.
 *
 * Toolbar: select (default) · surface create (plane fit / 2D edge) · camera
 * orientation · surface draw (auto) · manual surface draw.
 *
 * Tool availability is managed by SurfacePersona:
 * - SelectionPersonaTool: always available (default enabled)
 * - Draw tools (extend SurfaceToolBase): only enabled when a surface is selected
 */

import { IPersonaConfig, ToolbarElementType } from '../../types';
import { ViewModes } from '../../helpers';
import { SelectionPersonaTool, SurfaceFitPersonaTool, SurfaceCameraOrientationTool, SurfaceHostTool, ManualSurfaceHostTool } from '../../tools';
import { SurfacePersona } from '../../personas/implementations/SurfacePersona';

export const SurfacePersonaConfig: IPersonaConfig = {
  name: 'surface',
  priority: 85,
  exclusive: false,
  factory: (viewer, config, viewManager) => new SurfacePersona(viewer, config, viewManager),

  defaultViewConfig: {
    views: [ViewModes.PERSPECTIVE_3D_RGB, ViewModes.ORTHOGRAPHIC_2D_TOP],
    defaultViewId: 'perspective_3d_rgb',
  },

  modes: [
    {
      id: 'default',
      name: 'surface',
      toolbarElements: [
        // Real selection tool (shared with Monitoring) — selects surface groups.
        {
          type: ToolbarElementType.TOOL,
          config: {
            id: 'selection',
            factory: (viewer, persona) => new SelectionPersonaTool(viewer, persona),
            defaultEnabled: true,
            exclusive: true,
            icon: 'select',
            name: 'select',
          },
        },
        {
          type: ToolbarElementType.SPACER,
          size: 'medium',
        },
        // Surface creation — fit a plane in 3D, or click an edge in 2D (shared with
        // Monitoring; dispatches create_surface).
        {
          type: ToolbarElementType.TOOL,
          config: {
            id: 'plane',
            factory: (viewer, persona) => new SurfaceFitPersonaTool(viewer, persona),
            exclusive: true,
            icon: 'plane',
            name: 'surface',
          },
        },
        // Camera orientation — align the 2D view roll by drawing a line (floor-free).
        {
          type: ToolbarElementType.TOOL,
          config: {
            id: 'surface_camera_orientation',
            factory: (viewer, persona) => new SurfaceCameraOrientationTool(viewer, persona),
            exclusive: true,
            icon: 'undo-dot',
            name: 'camera orientation',
          },
        },
        {
          type: ToolbarElementType.SPACER,
          size: 'medium',
        },
        // Surface draw tool (auto/manual via the mode toggle in its settings) -
        // requires surface selection. Hosts the shared LineTool subtool with a
        // SurfaceKindAdapter; replaces the former auto_surface + manual_surface_draw pair.
        {
          type: ToolbarElementType.TOOL,
          config: {
            id: 'surface_line',
            factory: (viewer, persona) => new SurfaceHostTool(viewer, persona),
            defaultEnabled: false,
            defaultDisabled: true, // Requires surface selection
            exclusive: true,
            icon: 'Surface-detection-automatisch',
            name: 'surface',
            disabledTooltip: 'select a surface first',
          },
        },
        // Manual surface draw — click-to-place points (MeasureEngine) on the
        // surface plane, committed via the same SurfaceKindAdapter.
        {
          type: ToolbarElementType.TOOL,
          config: {
            id: 'manual_surface_draw',
            factory: (viewer, persona) => new ManualSurfaceHostTool(viewer, persona),
            defaultEnabled: false,
            defaultDisabled: true, // Requires surface selection
            exclusive: true,
            icon: 'Surface-detection-manueel',
            name: 'manual surface draw',
            disabledTooltip: 'select a surface first',
          },
        },
        // Spacer before undo/redo
        {
          type: ToolbarElementType.SPACER,
          size: 'medium',
        },
        // Undo action
        {
          type: ToolbarElementType.ACTION,
          id: 'undo',
          icon: 'undo',
          tooltip: 'undo',
        },
        // Redo action
        {
          type: ToolbarElementType.ACTION,
          id: 'redo',
          icon: 'redo',
          tooltip: 'redo',
        },
      ],
    },
  ],
};
